import React from 'react';
import PropTypes from 'prop-types';

/**
 * COMPONENTE: QuickReactionPersonalMetricsRow
 * [HU-06] Reacción Rápida Personal - Barra de 10 KPIs de Telemetría
 *
 * Muestra una fila deslizable horizontalmente (overflow-x: auto) con 10 tarjetas métricas:
 * 1. Escaneos QR (qr_scan)
 * 2. Vistas de Página (page_view)
 * 3. Inicios Formulario (form_start)
 * 4. Formularios Completos (form_complete)
 * 5. Partidas Iniciadas (game_start)
 * 6. Triggers Disparados (trigger_fired)
 * 7. Partidas Completadas (game_complete)
 * 8. Mejor Récord (ms)
 * 9. Tiempo Promedio (ms)
 * 10. Tasa de Éxito (%)
 */
export const QuickReactionPersonalMetricsRow = ({ stats = {} }) => {
    // Extracción segura de conteos de telemetría y métricas agregadas
    const telemetry = stats?.telemetryStepCounts || {};

    const qrScanCount = telemetry.qr_scan ?? stats?.qr_scan ?? 0;
    const pageViewCount = telemetry.page_view ?? stats?.page_view ?? 0;
    const formStartCount = telemetry.form_start ?? stats?.form_start ?? 0;
    const formCompleteCount = telemetry.form_complete ?? stats?.form_complete ?? stats?.totalRegistered ?? 0;
    const gameStartCount = telemetry.game_start ?? stats?.game_start ?? 0;
    const triggerFiredCount = telemetry.trigger_fired ?? stats?.trigger_fired ?? 0;
    const gameCompleteCount = telemetry.game_complete ?? stats?.game_complete ?? stats?.totalPlayed ?? 0;

    const bestMs = stats?.bestReactionTimeMs && stats.bestReactionTimeMs > 0
        ? `${stats.bestReactionTimeMs} ms`
        : '—';

    const avgMs = stats?.averageReactionTimeMs && stats.averageReactionTimeMs > 0
        ? `${Math.round(stats.averageReactionTimeMs)} ms`
        : '—';

    const successRate = formCompleteCount > 0
        ? `${((gameCompleteCount / formCompleteCount) * 100).toFixed(1)}%`
        : '0.0%';

    const metricCards = [
        {
            id: 'qr_scan',
            title: 'Escaneos QR',
            value: qrScanCount,
            subtitle: 'Lecturas de código',
            icon: 'bi-qr-code-scan',
            gradient: 'qr'
        },
        {
            id: 'page_view',
            title: 'Vistas de Página',
            value: pageViewCount,
            subtitle: 'Visitas móviles',
            icon: 'bi-eye-fill',
            gradient: 'page'
        },
        {
            id: 'form_start',
            title: 'Inicios Form.',
            value: formStartCount,
            subtitle: 'Comenzaron registro',
            icon: 'bi-pencil-square',
            gradient: 'start'
        },
        {
            id: 'form_complete',
            title: 'Forms. Completos',
            value: formCompleteCount,
            subtitle: 'Tokens OTT emitidos',
            icon: 'bi-check2-circle',
            gradient: 'complete'
        },
        {
            id: 'game_start',
            title: 'Partidas Iniciadas',
            value: gameStartCount,
            subtitle: 'Cuenta regresiva',
            icon: 'bi-play-circle-fill',
            gradient: 'game'
        },
        {
            id: 'trigger_fired',
            title: 'Triggers Disparados',
            value: triggerFiredCount,
            subtitle: 'Señal de reacción',
            icon: 'bi-lightning-charge-fill',
            gradient: 'trigger'
        },
        {
            id: 'game_complete',
            title: 'Partidas Completas',
            value: gameCompleteCount,
            subtitle: 'Jugadas procesadas',
            icon: 'bi-trophy-fill',
            gradient: 'played'
        },
        {
            id: 'best_record',
            title: 'Mejor Récord',
            value: bestMs,
            subtitle: 'Récord histórico',
            icon: 'bi-stopwatch-fill',
            gradient: 'record'
        },
        {
            id: 'avg_time',
            title: 'Tiempo Promedio',
            value: avgMs,
            subtitle: 'Velocidad media',
            icon: 'bi-speedometer2',
            gradient: 'avg'
        },
        {
            id: 'success_rate',
            title: 'Tasa de Éxito',
            value: successRate,
            subtitle: 'Forms a jugada',
            icon: 'bi-graph-up-arrow',
            gradient: 'rate'
        }
    ];

    return (
        <div className="QrpMetricsRow">
            <div className="QrpMetricsRow__ScrollTrack">
                {metricCards.map((card) => (
                    <div
                        key={card.id}
                        className={`QrpMetricsRow__Card QrpMetricsRow__Card--${card.gradient}`}
                    >
                        <div className="QrpMetricsRow__IconWrapper">
                            <i className={`bi ${card.icon}`} />
                        </div>
                        <div className="QrpMetricsRow__Content">
                            <span className="QrpMetricsRow__Title">{card.title}</span>
                            <span className="QrpMetricsRow__Value">{card.value}</span>
                            <span className="QrpMetricsRow__Subtitle">{card.subtitle}</span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

QuickReactionPersonalMetricsRow.propTypes = {
    stats: PropTypes.shape({
        totalRegistered: PropTypes.number,
        totalPlayed: PropTypes.number,
        totalDisqualified: PropTypes.number,
        averageReactionTimeMs: PropTypes.number,
        bestReactionTimeMs: PropTypes.number,
        telemetryStepCounts: PropTypes.object,
        qr_scan: PropTypes.number,
        page_view: PropTypes.number,
        form_start: PropTypes.number,
        form_complete: PropTypes.number,
        game_start: PropTypes.number,
        trigger_fired: PropTypes.number,
        game_complete: PropTypes.number
    })
};

export default QuickReactionPersonalMetricsRow;
